/* ==========================================================================
   Offer / Combo Cards  ·  assets/offer-cards.js
   --------------------------------------------------------------------------
   Behaviour for snippets/offer-cards.liquid.

   Single source of truth for quantity = the existing #Quantity-{section} input.
   Selecting a card writes that input's value and dispatches the native `change`
   event the theme already listens for — the SAME contract the Sticky Product Bar
   uses. So:
     • product-form.js posts the right quantity,
     • <quantity-input> re-validates,
     • the Sticky Product Bar auto-updates (it already listens to that input).

   Cards never create a second form / quantity / variant picker. Shopify's native
   automatic discount (Buy X Get Y) does the real discounting at checkout.

   No jQuery. No MutationObserver. Delegated listeners. Cached selectors.
   Re-prices from the live variant via the theme's `variantChange` event.
   ========================================================================== */

if (!customElements.get('offer-cards')) {
  customElements.define(
    'offer-cards',
    class OfferCards extends HTMLElement {
      constructor() {
        super();
        this.section = this.dataset.section;
        this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';

        // Live pricing (cents). Updated on every variantChange.
        this.unitPrice = parseInt(this.dataset.unitPrice, 10) || 0;
        this.refPrice = parseInt(this.dataset.comparePrice, 10) || this.unitPrice;

        // Guard so our own quantity write doesn't re-trigger reverse-sync work.
        this._syncingQty = false;

        this._onRootChange = this._onRootChange.bind(this);
        this._onRealQtyChange = this._onRealQtyChange.bind(this);
      }

      /* -------------------------------------------------------------------- */
      /*  Lifecycle                                                           */
      /* -------------------------------------------------------------------- */
      connectedCallback() {
        this.cards = Array.from(this.querySelectorAll('.offer-card'));
        if (!this.cards.length) return;

        // The one authoritative quantity input (shared with the Sticky Bar).
        this.realQtyInput = document.getElementById(`Quantity-${this.section}`);

        // Delegated selection listener (one handler for all radios).
        this.addEventListener('change', this._onRootChange);

        // Reverse-sync: any quantity change elsewhere → highlight matching card.
        if (this.realQtyInput) {
          this.realQtyInput.addEventListener('change', this._onRealQtyChange);
        }

        // Re-price when the variant changes.
        this._subscribe();

        // Normalise first paint (idempotent) + align selection with the form.
        this._repriceAll();
        this._syncSelectionFromForm();
      }

      disconnectedCallback() {
        this.removeEventListener('change', this._onRootChange);
        if (this.realQtyInput) {
          this.realQtyInput.removeEventListener('change', this._onRealQtyChange);
        }
        if (this._variantUnsub) this._variantUnsub();
      }

      _subscribe() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
        this._variantUnsub = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          this._onVariantChange(event);
        });
      }

      /* -------------------------------------------------------------------- */
      /*  Selection → quantity                                                */
      /* -------------------------------------------------------------------- */
      _onRootChange(event) {
        const radio = event.target.closest('.offer-card__radio');
        if (!radio || !radio.checked) return;
        const card = radio.closest('.offer-card');
        if (!card) return;
        this._markSelected(card);
        this._pushQty(parseInt(card.dataset.quantity, 10));
      }

      // Mirror the checked radio to the .offer-card--selected class (covers
      // browsers without :has(), and keeps ARIA state on the radios themselves).
      _markSelected(selectedCard) {
        this.cards.forEach((card) => {
          const isSelected = card === selectedCard;
          card.classList.toggle('offer-card--selected', isSelected);
          const radio = card.querySelector('.offer-card__radio');
          if (radio) radio.checked = isSelected;
        });
      }

      // Write the combo quantity to the real input + fire the native `change`.
      // This is the shared quantity contract used by the Sticky Product Bar.
      _pushQty(quantity) {
        if (!this.realQtyInput || isNaN(quantity)) return;

        // Respect the form's own max (inventory / quantity rules) if present.
        const max =
          this.realQtyInput.max !== '' ? parseInt(this.realQtyInput.max, 10) : null;
        let value = quantity;
        if (max !== null && value > max) value = max;

        this._syncingQty = true;
        this.realQtyInput.value = value;
        this.realQtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        this._syncingQty = false;
      }

      /* -------------------------------------------------------------------- */
      /*  Quantity → selection (reverse sync)                                 */
      /* -------------------------------------------------------------------- */
      _onRealQtyChange() {
        if (this._syncingQty) return; // ignore our own write
        this._syncSelectionFromForm();
      }

      _syncSelectionFromForm() {
        if (!this.realQtyInput) return;
        const value = parseInt(this.realQtyInput.value, 10);

        // Highlight the card whose total quantity matches; if none matches
        // (a custom manual quantity), deselect all — no false highlight.
        this.cards.forEach((card) => {
          const isMatch = parseInt(card.dataset.quantity, 10) === value;
          card.classList.toggle('offer-card--selected', isMatch);
          const radio = card.querySelector('.offer-card__radio');
          if (radio) radio.checked = isMatch;
        });
      }

      /* -------------------------------------------------------------------- */
      /*  Variant change → re-price every card                                */
      /* -------------------------------------------------------------------- */
      _onVariantChange(event) {
        if (!event || !event.data || event.data.sectionId !== this.section) return;
        const variant = event.data.variant;
        if (!variant) return;

        this.unitPrice = variant.price;
        const compare = variant.compare_at_price;
        this.refPrice = compare && compare > variant.price ? compare : variant.price;

        this._repriceAll();
      }

      _repriceAll() {
        this.cards.forEach((card) => this._repriceCard(card));
      }

      _repriceCard(card) {
        const qty = parseInt(card.dataset.quantity, 10);
        const free = parseInt(card.dataset.free, 10) || 0;
        if (isNaN(qty) || qty <= 0) return;

        const paidUnits = Math.max(qty - free, 0);
        const total = paidUnits * this.unitPrice;
        const reference = qty * this.refPrice;
        const savings = Math.max(reference - total, 0);
        const perUnit = Math.round(total / qty);
        const discountPct =
          reference > 0 && savings > 0 ? Math.round((savings * 100) / reference) : 0;

        // Total (always shown).
        this._setText(card, '[data-offer-total]', this._formatMoney(total));

        // Struck reference — only when it beats the total.
        const refEl = card.querySelector('[data-offer-reference]');
        if (refEl) {
          if (reference > total) {
            refEl.textContent = this._formatMoney(reference);
            refEl.hidden = false;
          } else {
            refEl.hidden = true;
          }
        }

        // Effective per-unit.
        this._setText(card, '[data-offer-perunit]', this._formatMoney(perUnit));

        // Savings pill.
        const savingsEl = card.querySelector('[data-offer-savings]');
        if (savingsEl) {
          if (savings > 0) {
            this._setText(card, '[data-offer-savings-amount]', this._formatMoney(savings));
            savingsEl.hidden = false;
          } else {
            savingsEl.hidden = true;
          }
        }

        // Discount %.
        const discountEl = card.querySelector('[data-offer-discount]');
        if (discountEl) {
          if (discountPct > 0) {
            this._setText(card, '[data-offer-discount-value]', discountPct);
            discountEl.hidden = false;
          } else {
            discountEl.hidden = true;
          }
        }
      }

      /* -------------------------------------------------------------------- */
      /*  Utilities                                                           */
      /* -------------------------------------------------------------------- */
      _setText(card, selector, text) {
        const el = card.querySelector(selector);
        if (el) el.textContent = text;
      }

      // Minimal Shopify-compatible money formatter (no library, no jQuery).
      _formatMoney(cents) {
        // Strip any HTML wrapper (e.g. <span class="money">) — we set textContent.
        const format = this.moneyFormat.replace(/<[^>]+>/g, '');
        const placeholder = /\{\{\s*(\w+)\s*\}\}/;

        const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
          if (isNaN(number) || number == null) return '0';
          const fixed = (number / 100.0).toFixed(precision);
          const parts = fixed.split('.');
          const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
          const frac = parts[1] ? decimal + parts[1] : '';
          return whole + frac;
        };

        const match = format.match(placeholder);
        if (!match) return format;

        let value;
        switch (match[1]) {
          case 'amount':
            value = formatWithDelimiters(cents, 2);
            break;
          case 'amount_no_decimals':
            value = formatWithDelimiters(cents, 0);
            break;
          case 'amount_with_comma_separator':
            value = formatWithDelimiters(cents, 2, '.', ',');
            break;
          case 'amount_no_decimals_with_comma_separator':
            value = formatWithDelimiters(cents, 0, '.', ',');
            break;
          case 'amount_with_apostrophe_separator':
            value = formatWithDelimiters(cents, 2, "'", '.');
            break;
          case 'amount_with_space_separator':
            value = formatWithDelimiters(cents, 2, ' ', '.');
            break;
          case 'amount_no_decimals_with_space_separator':
            value = formatWithDelimiters(cents, 0, ' ', '');
            break;
          default:
            value = formatWithDelimiters(cents, 2);
        }
        return format.replace(placeholder, value);
      }
    }
  );
}